import { Button, Stack, TextField } from '@mui/material';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { MONTH, legacyFrom, legacyThrough, monthEnd, monthStart, periodError, todayUtc } from './report-format.js';

/**
 * The date range shared by the financial report tabs (DEV-148): inclusive UTC days in
 * the `from`/`through` URL params. Legacy YYYY-MM links are rewritten in place.
 */

export interface ReportPeriod {
  from: string;
  through: string;
  today: string;
  /** Why the period cannot be reported, or null when it is valid. */
  error: string | null;
}

export function useReportPeriod(): ReportPeriod {
  const [params, setParams] = useSearchParams();
  const today = todayUtc();
  const rawFrom = params.get('from');
  const rawThrough = params.get('through');
  const from = rawFrom ? legacyFrom(rawFrom) : monthStart(today);
  const through = rawThrough ? legacyThrough(rawThrough, today) : today;
  const legacy = (rawFrom !== null && MONTH.test(rawFrom)) || (rawThrough !== null && MONTH.test(rawThrough));

  useEffect(() => {
    if (!legacy) return;
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.set('from', from);
      next.set('through', through);
      return next;
    }, { replace: true });
  }, [legacy, from, through, setParams]);

  return { from, through, today, error: periodError(from, through, today) };
}

interface QuickPick { label: string; from: string; through: string }

function quickPicks(today: string): QuickPick[] {
  const lastMonthStart = monthStart(today, -1);
  return [
    { label: 'This month', from: monthStart(today), through: today },
    { label: 'Last month', from: lastMonthStart, through: monthEnd(lastMonthStart.slice(0, 7)) },
    { label: 'Year to date', from: `${today.slice(0, 4)}-01-01`, through: today },
  ];
}

interface FieldsProps {
  period: ReportPeriod;
  /** Writes the new `from`/`through` (an empty value clears the param). */
  onChange: (range: Record<string, string>) => void;
}

export function ReportPeriodFields({ period, onChange }: FieldsProps): JSX.Element {
  const { from, through, today } = period;
  return <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
    <TextField size="small" type="date" label="From (UTC)" value={from} InputLabelProps={{ shrink: true }}
      inputProps={{ max: through < today ? through : today }} onChange={(event) => onChange({ from: event.target.value })} />
    <TextField size="small" type="date" label="To (UTC)" value={through} InputLabelProps={{ shrink: true }}
      inputProps={{ min: from, max: today }} onChange={(event) => onChange({ through: event.target.value })} />
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap role="group" aria-label="Quick periods">
      {quickPicks(today).map((pick) => {
        const active = pick.from === from && pick.through === through;
        return <Button key={pick.label} size="small" variant={active ? 'contained' : 'outlined'} aria-pressed={active}
          onClick={() => onChange({ from: pick.from, through: pick.through })}>{pick.label}</Button>;
      })}
    </Stack>
  </Stack>;
}
